import React, { useState } from 'react';
import { Layout } from './components/Layout';
import { ShortcutTable } from './components/ShortcutTable';
import { NewShortcutModal } from './components/NewShortcutModal';
import { useAuth } from './hooks/useAuth';
import { useShortcuts } from './hooks/useShortcuts';
import { useUserPreferences } from './hooks/useUserPreferences';
import { Shortcut } from './types';
import { Plus, Download, Upload, RefreshCw, Printer } from 'lucide-react';
import { generateShortcutsPDF, generateShortcutsFullPagePDF } from './lib/printUtils';
import { DebugFirestore } from './components/DebugFirestore';
import './App.css';

function App() {
  const { user } = useAuth();
  const { shortcuts, loading: shortcutsLoading, error: shortcutsError, createShortcut, updateShortcut, deleteShortcut } = useShortcuts(user?.uid || null);
  const { preferences, loading: prefsLoading, toggleShortcut, updateCustomExpansion } = useUserPreferences(user?.uid || null);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingShortcut, setEditingShortcut] = useState<Shortcut | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  const handleCreateShortcut = async (shortcut: string, expansion: string, isTemplate: boolean) => {
    await createShortcut(shortcut, expansion, isTemplate);
  };

  const handleEditShortcut = async (shortcut: string, expansion: string) => {
    if (editingShortcut) {
      await updateShortcut(editingShortcut.id, expansion);
    }
  };

  const handleDeleteShortcut = async (shortcut: Shortcut) => {
    if (window.confirm(`Are you sure you want to delete the shortcut "${shortcut.shortcut}"?`)) {
      await deleteShortcut(shortcut.id);
    }
  };

  const handleEdit = (shortcut: Shortcut) => {
    setEditingShortcut(shortcut);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingShortcut(null);
  };

  const handleExport = () => {
    const exportData = shortcuts.map(s => ({
      shortcut: s.shortcut,
      expansion: s.expansion,
      isTemplate: s.isTemplate || false
    }));
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gd-shortcuts-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      
      try {
        const text = await file.text();
        const data = JSON.parse(text);
        
        if (!Array.isArray(data)) {
          throw new Error('Invalid file format');
        }
        
        let imported = 0;
        let failed = 0;
        
        for (const item of data) {
          if (item.shortcut && item.expansion) {
            try {
              await createShortcut(item.shortcut, item.expansion, item.isTemplate || false);
              imported++;
            } catch (error) {
              failed++;
            }
          }
        }
        
        alert(`Import complete!\nImported: ${imported}\nFailed: ${failed}`);
      } catch (error) {
        alert('Failed to import shortcuts. Please check the file format.');
      }
    };
    input.click();
  };

  const handleSync = async () => {
    setIsSyncing(true);
    // Simulate sync - in real implementation, this would trigger extension sync
    setTimeout(() => {
      setIsSyncing(false);
      alert('Sync request sent. The extension will update within 5 minutes.');
    }, 1000);
  };

  const handlePrint = () => {
    console.log('Print button clicked');
    console.log('User email:', user?.email);
    console.log('Shortcuts count:', shortcuts.length);
    console.log('Preferences:', preferences);
    
    if (user?.email) {
      try {
        generateShortcutsPDF(shortcuts, preferences, user.email);
      } catch (error) {
        console.error('Print error:', error);
        alert('Error generating PDF: ' + (error as Error).message);
      }
    } else {
      alert('User email not found');
    }
  };

  const handlePrintFullPage = () => {
    console.log('Print full page button clicked');
    console.log('User email:', user?.email);
    console.log('Shortcuts count:', shortcuts.length);
    console.log('Preferences:', preferences);
    
    if (user?.email) {
      try {
        generateShortcutsFullPagePDF(shortcuts, preferences, user.email);
      } catch (error) {
        console.error('Print error:', error);
        alert('Error generating PDF: ' + (error as Error).message);
      }
    } else {
      alert('User email not found');
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="loading-message">Please sign in to manage shortcuts</div>
      </Layout>
    );
  }

  const loading = shortcutsLoading || prefsLoading;

  return (
    <Layout>
      <div className="app-content">
        <div className="action-bar">
          <button className="action-btn primary" onClick={() => setIsModalOpen(true)}>
            <Plus size={20} />
            New Shortcut
          </button>
          <button className="action-btn" onClick={handleImport}>
            <Upload size={20} />
            Import
          </button>
          <button className="action-btn" onClick={handleExport} disabled={shortcuts.length === 0}>
            <Download size={20} />
            Export
          </button>
          <button className="action-btn" onClick={handleSync} disabled={isSyncing}>
            <RefreshCw size={20} className={isSyncing ? 'spinning' : ''} />
            Sync Now
          </button>
          <button className="action-btn" onClick={handlePrint} disabled={shortcuts.length === 0}>
            <Printer size={20} />
            Print (4x6)
          </button>
          <button className="action-btn" onClick={handlePrintFullPage} disabled={shortcuts.length === 0}>
            <Printer size={20} />
            Print (full page)
          </button>
        </div>

        {shortcutsError && (
          <div className="error-banner">
            {shortcutsError}
          </div>
        )}

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading shortcuts...</p>
          </div>
        ) : (
          <ShortcutTable
            shortcuts={shortcuts}
            preferences={preferences}
            currentUserId={user.uid}
            currentUserEmail={user.email || undefined}
            onToggle={toggleShortcut}
            onEdit={handleEdit}
            onDelete={handleDeleteShortcut}
            onUpdateCustomExpansion={updateCustomExpansion}
          />
        )}

        <NewShortcutModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSave={editingShortcut ? handleEditShortcut : handleCreateShortcut}
          editingShortcut={editingShortcut}
        />
        
        {/* Debug panel - only show when enabled */}
        {showDebug && <DebugFirestore />}
        
        {/* Debug toggle button */}
        <button 
          className="debug-toggle"
          onClick={() => setShowDebug(!showDebug)}
          title={showDebug ? "Hide debug info" : "Show debug info"}
        >
          🐛
        </button>
      </div>
    </Layout>
  );
}

export default App;
